import React, {
    useContext,
    useState,
    useEffect,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from "react-router";

import { getBasketData, refetchBasketData, getCustomer, NotificationContext, handleError } from "../router"
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";

import DonationPrompt from './donation-prompt';
import MembershipPrompt from './membership-prompt';
import SavingsPrompt from './savings-prompt';
import BasketEvent from './basket-event';
import AdditionalDonation from './additional-donation';
import BasketSummary from '../components/basket-summary';
import TopPicks from '../components/top-picks';
import LoadingIndicator from '../components/loading-indicator';
import CrossIcon from '../svg/cross';

export default function Basket() {

    const {notification, setNotification} = useContext(NotificationContext);

    const [groupedEvents, setGroupedEvents] = useState(null);
    const [patchBody, setPatchBody] = useState(null);
    const [hasDonationUpsell, setHasDonationUpsell] = useState(false);
    const [page, setPage] = useState("basket");
    const [excludedDonations, setExcludedDonations] = useState([]);

    const { data: basketData, isPending: basketDataPending } = getBasketData();
    const { data: customer, isPending: customerPending } = getCustomer();

    useEffect(() => {
        if (!basketData || !basketData.tickets) return;
        
        const grouped_tickets = UTILS.groupTicketsByInstance(basketData.tickets);
        setGroupedEvents(grouped_tickets);

        const has_upsell = UTILS.ticketsHaveDonationUpsell(basketData.tickets);
        setHasDonationUpsell(has_upsell);

        const excluded_donations = UTILS.getExcludedDonations(basketData);
        setExcludedDonations(excluded_donations);

    }, [basketData]);
        
    const removeDonation = useMutation({
        mutationFn: (donationId) => QUERIES.removeDonation(donationId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    });
        
    const removeMembership = useMutation({
        mutationFn: (donationId) => QUERIES.removeMembership(donationId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    });
        
    const removeGiftVoucher = useMutation({
        mutationFn: (giftVoucherId) => QUERIES.removeGiftVoucher(giftVoucherId),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            refetchBasketData();
        },
    });

    const patchBasket = useMutation({
        mutationFn: () => QUERIES.patchBasket(patchBody),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchBasketData();
        },
    })

    const patchMembership = useMutation({
        mutationFn: ({subscriptionId, body}) => QUERIES.patchMembership(subscriptionId, body),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchBasketData();
        },
    })

    const applyPromoCode = (formData) => {
        const code = formData.get("promocode");
        if (!code) return;
        setPatchBody({ PromoCode : code });
    }

    useEffect(() => {
        if (!patchBody) return;
        patchBasket.mutateAsync();
    }, [patchBody])

    useEffect(() => {
        refetchBasketData();
    }, [])
    
    
    if (basketDataPending || customerPending) return <LoadingIndicator type="page" />;

    return (
        <div className="booking-pathway__container booking-pathway__basket">
            <div className='booking-pathway__main-content'>
                {page == "basket" ?
                    <>
                        <h1 className="booking-pathway__header">Order summary</h1>

                        { !basketDataPending && (!groupedEvents || !groupedEvents.length) && !basketData?.membershipSubscriptions.length && !basketData?.donations.length && !basketData?.giftVouchers.length &&
                            <p>There are no items in your basket.</p>
                        }

                        <div className='basket-items'>
                            {groupedEvents?.map((group) => (
                                <BasketEvent key={group.event_id} event={group} />
                            ))}

                            {basketData?.membershipSubscriptions.length ? 
                                basketData.membershipSubscriptions.map((membership) => (

                                    <div key={membership.id} className='basket-item basket-item__membership'>
                                            <h2 className='basket-item__title'>{membership.membership.name} Membership</h2>
                                            <p className='basket-item__info'>
                                                <span>
                                                    £{membership.membership.price.toFixed(2)}
                                                    {membership.autoRenew && (
                                                    <span className="form-field--checkbox">
                                                        <input type="checkbox" 
                                                                id={`membershipautorenew-${membership.id}`}
                                                                value={membership.id}
                                                                defaultChecked={membership.autoRenew}
                                                                // disabled={patchMembership.isPending}
                                                                // onChange={(e) =>
                                                                //     patchMembership.mutateAsync({
                                                                //         subscriptionId: membership.id,
                                                                //         body: [{ autoRenew: e.target.checked }]
                                                                //     })
                                                                // } 
                                                                // disabled
                                                                />
                                                        <label htmlFor={`membershipautorenew-${membership.id}`}>Automatically renew and store card details</label>
                                                    </span>
                                                    )}
                                                </span>

                                                <button type="button" 
                                                        onClick={() => removeMembership.mutateAsync(membership.id)} 
                                                        className='btn btn--remove'>
                                                    <span className='sr-text'>Remove membership</span>
                                                    <CrossIcon />
                                                </button>    
                                            </p>
                                    </div>
                                ))
                                : null
                            }

                            {basketData.donations.length > 0 &&
                                basketData.donations.map(donation => {
                                    if (excludedDonations.includes(donation.id)) return null;

                                    return (
                                    <div key={donation.id} className='basket-item basket-item__donation'>
                                        <h2 className='basket-item__title'>Donation</h2>
                                        <p className='basket-item__info'>
                                        £{donation.amount.toFixed(2)}
                                        <button
                                            type="button"
                                            onClick={() => removeDonation.mutateAsync(donation.id)}
                                            className='btn btn--remove'
                                        >
                                            <span className='sr-text'>Remove donation</span>
                                            <CrossIcon />
                                        </button>
                                        </p>
                                    </div>
                                    );
                                })
                            }

                            {basketData.giftVouchers.length ? 
                                basketData.giftVouchers.map((voucher) => {
                                    const formattedDate = UTILS.formatDateTime(voucher.sendDate);
                                
                                // console.log('GIFT', basketData.giftVouchers)  

                                return (
                                    <div key={voucher.id} className='basket-item basket-item__gift-voucher'>
                                            <h2 className='basket-item__title'>
                                                {
                                                // Friend
                                                voucher.amount === 42
                                                ? 'Friend membership gift voucher'
                                                // Support Patron
                                                : voucher.amount === 1001
                                                ? 'Supporting Patron membership gift voucher'
                                                // Patron
                                                : voucher.amount === 500
                                                ? 'Patron membership gift voucher'
                                                //
                                                : voucher.amount === 5000
                                                ? 'Principle Patron membership gift voucher'
                                                //
                                                : voucher.amount === 84
                                                ? 'Leading Friend membership gift voucher'
                                                // Else Standard
                                                : `Gift voucher`
                                                }
                                            </h2>

                                            <div className='basket-item__info'>
                                                <p className='basket-item__info--breakdown'>
                                                    {voucher.toName &&
                                                        <span><strong>To:</strong> {voucher.toName} {formattedDate ? `to be sent on ${formattedDate}` : ""}</span>
                                                    }
                                                    {voucher.fromName &&
                                                        <span><strong>From:</strong> {voucher.fromName}</span>
                                                    }
                                                    {voucher.deliveryEmailAddress &&
                                                        <span>{voucher.deliveryEmailAddress}</span>
                                                    }

                                                    £{voucher.amount.toFixed(2)}
                                                </p>
                                                
                                                <button type="button" 
                                                        onClick={() => removeGiftVoucher.mutateAsync(voucher.id)} 
                                                        className='btn btn--remove'>
                                                    <span className='sr-text'>Remove gift voucher</span>
                                                    <CrossIcon />
                                                </button>    
                                            </div>
                                    </div>
                                )}) 
                                : null
                            }
                        </div>
                        
                        {customer ?
                            <div className='basket-top-picks'>
                                <TopPicks origin="basket" customer={customer} />
                            </div>
                        :null}
                    </>
                :
                page == "additional-donation" ?
                    <AdditionalDonation />
                :null
                }
                
            </div>

            <div className='booking-pathway__summary'>

                {customer?.subscriptions?.length ?
                    /* If user is already a member upsell a donation */
                    customer.subscriptions.some(
                        sub => 
                            sub.membership?.id === "48ANVGBVGSCDKJGMNMBJKCJQDQVPRGKQC" ||
                            sub.membership?.id === "201AGBHDRLQHNHPHKKMPKLGPMDRDTDMVL"
                    ) ? (
                        <SavingsPrompt basketData={basketData} />
                    ) : null
                :
                    /* If user is not a member upsell membership */
                    <MembershipPrompt basketData={basketData} />
                }


                <section className='booking-pathway__promo-code'>
                    <form action={applyPromoCode}>
                        <label className='booking-pathway__header' htmlFor="promoInput">Promotion code</label>
                        <div className='booking-pathway__promo-code--input'>
                            <input id="promoInput" type="text" name="promocode" />
                            <button className='btn' type="submit" value="Apply">
                                <span className="sr-only">Apply promo code</span>
                                Apply
                            </button>
                        </div>
                    </form>
                </section>

                <h2 className='sr-text'>Basket summary</h2>
                <BasketSummary />

            </div>

            
            <div className='booking-pathway__footer'>
                {!basketData ?
                    <p className='btn btn--primary disabled'>Checkout</p>
                : 
                page == "basket" && hasDonationUpsell ?
                    <button type="button" className='btn btn--primary' onClick={() => setPage("additional-donation")}>Continue</button>
                :
                    <Link to="/checkout" className='btn btn--primary' onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Checkout</Link>
                }
                <a href="whats-on" className='btn'>Continue browsing</a>
            </div>
        </div>
    );
}